import { datadogRum } from "@datadog/browser-rum";
import Decimal from "decimal.js";

import {
  HypotheticalPortfolioAssignment,
  Response as HypotheticalResponse,
} from "@fartherfinance/frontend/api/Investment/request/postHypotheticalProposalV2";
import { RequestConfig } from "@fartherfinance/frontend/api/Types";

import {
  HypotheticalTradeGroup,
  SecuritiesTable,
} from "@src/multiCustodian/components/TaxBudgets/reducer/types";

async function retrieveHypotheticalAssignments(
  hypotheticalTradeGroups: HypotheticalTradeGroup[],
  auth: RequestConfig
): Promise<HypotheticalPortfolioAssignment[]> {
  if (auth === null) {
    throw new Error("auth is not defined");
  }

  const portfolioAssignmentsWithEntries = hypotheticalTradeGroups.map((hyp) => {
    if (hyp.hypotheticalPortfolioModelId === undefined) {
      throw new Error("hypotheticalPortfolioModelId cannot be undefined");
    }

    return {
      type: "planId" as const,
      identifyingName: hyp.hypotheticalPortfolioModelId.label,
      virtualAccountIds: hyp.virtualAccountIds,
      portfolioPlanId: hyp.hypotheticalPortfolioModelId.id,
    } as HypotheticalPortfolioAssignment;
  });

  return portfolioAssignmentsWithEntries;
}

type capitalConsequences = {
  estimatedLongTermGainsDollars: Decimal;
  estimatedLongTermLossesDollars: Decimal;
  estimatedLongTermTaxesDollars: Decimal;
  estimatedShortTermGainsDollars: Decimal;
  estimatedShortTermLossesDollars: Decimal;
  estimatedShortTermTaxesDollars: Decimal;
};

type HypotheticalProposal = {
  totalGainsRealized?: Decimal;
  totalLossesHarvested?: Decimal;
  totalTaxesPaid?: Decimal;
};

function transformHypotheticalProposal(
  array: {
    capitalConsequences: capitalConsequences;
  }[]
): HypotheticalProposal {
  return array.reduce<HypotheticalProposal>((acc, proposal) => {
    const capitalConsequences = proposal.capitalConsequences;

    const hypotheticalGainsRealized = Decimal.sum(
      capitalConsequences.estimatedShortTermGainsDollars,
      capitalConsequences.estimatedLongTermGainsDollars
    );

    const hypotheticalLossesHarvested = Decimal.sum(
      capitalConsequences.estimatedShortTermLossesDollars,
      capitalConsequences.estimatedLongTermLossesDollars
    );

    const hypotheticalTaxesPaid = Decimal.sum(
      capitalConsequences.estimatedShortTermTaxesDollars,
      capitalConsequences.estimatedLongTermTaxesDollars
    );

    return {
      totalGainsRealized: acc.totalGainsRealized
        ? acc.totalGainsRealized.plus(hypotheticalGainsRealized)
        : hypotheticalGainsRealized,

      totalLossesHarvested: acc.totalLossesHarvested
        ? acc.totalLossesHarvested.plus(hypotheticalLossesHarvested)
        : hypotheticalLossesHarvested,

      totalTaxesPaid: acc.totalTaxesPaid
        ? acc.totalTaxesPaid.plus(hypotheticalTaxesPaid)
        : hypotheticalTaxesPaid,
    };
  }, {});
}

type ytdTaxConsumption = {
  netYtdLongTermGainOrLoss: Decimal;
  netYtdShortTermGainsOrLoss: Decimal;
  netYtdLongTermTaxes: Decimal;
  netYtdShortTermTaxes: Decimal;
  netYtdTaxBurden: Decimal;
};

function ytdNetGainsRealized(
  taxConsumptionArray: {
    ytdTaxConsumption: ytdTaxConsumption;
  }[]
): Decimal | undefined {
  if (taxConsumptionArray.length === 0) {
    return undefined;
  }

  return taxConsumptionArray.reduce<Decimal>((acc, proposal) => {
    const ytdTaxConsumption = proposal.ytdTaxConsumption;
    const sumGainOrLoss = Decimal.sum(
      ytdTaxConsumption.netYtdLongTermGainOrLoss,
      ytdTaxConsumption.netYtdShortTermGainsOrLoss
    );

    return acc.plus(sumGainOrLoss);
  }, new Decimal(0));
}

function createTradesTable(
  hypotheticalProposal: HypotheticalResponse,
  hypotheticalTradeGroup: HypotheticalTradeGroup
): SecuritiesTable {
  // Early exit if no proposals
  if (
    !hypotheticalProposal ||
    !hypotheticalProposal.proposalsWithTaxBudget?.proposals?.length ||
    !hypotheticalProposal.proposalsWithoutTaxBudget?.proposals?.length
  ) {
    console.warn("No proposals available for hypothetical analysis");
    return {};
  }

  const hypotheticalWithTaxBudget = hypotheticalProposal.proposalsWithTaxBudget;
  const hypotheticalWithoutTaxBudget =
    hypotheticalProposal.proposalsWithoutTaxBudget;

  const withoutTaxBudget = hypotheticalWithoutTaxBudget.proposals.find((p) => {
    const foundProposal = p.portfolioPlanAssignment.accounts.every((account) =>
      hypotheticalTradeGroup.virtualAccountIds.includes(account)
    );

    return foundProposal;
  })?.postTradeDetails;

  if (withoutTaxBudget === undefined) {
    datadogRum.addError("CreateHypothetical: withoutTaxBudget is undefined");
    console.error("withoutTaxBudget is undefined");
    return {}; // Exit early instead of throwing
  }

  const withoutTaxBudgetReduce = withoutTaxBudget?.reduce<SecuritiesTable>(
    (acc, trade): SecuritiesTable => {
      const securityObject = acc[trade.securityId];

      if (securityObject !== undefined) {
        // this should never happen
        datadogRum.addError("CreateHypothetical: securityId in first reduce");
        console.error("securityId already exists in acc", trade.securityId);
        return acc;
      }

      acc[trade.securityId] = {
        targetWeight: trade.portfolioWeight,
        securityId: trade.securityId,
        preWeight: trade.preWeight,
        postWeightWithoutTaxBudget: trade.postWeight,
        // omit from first iteration
        postWeightWithTaxBudget: new Decimal(0),
        portfolioWeightDelta: new Decimal(0),
      };
      return acc;
    },
    {}
  );

  const withTaxBudget = hypotheticalWithTaxBudget.proposals.find((p) => {
    const foundProposal = p.portfolioPlanAssignment.accounts.every((account) =>
      hypotheticalTradeGroup.virtualAccountIds.includes(account)
    );

    return foundProposal;
  })?.postTradeDetails;

  if (withTaxBudget === undefined) {
    datadogRum.addError("CreateHypothetical: withTaxBudget is undefined");
    console.error("withTaxBudget is undefined");
    return withoutTaxBudgetReduce; // Return what we have so far instead of throwing
  }

  const withTaxBudgetReduce = withTaxBudget.reduce<SecuritiesTable>(
    (acc, trade): SecuritiesTable => {
      const securityObject = acc[trade.securityId];

      if (securityObject === undefined) {
        // this should never happen
        datadogRum.addError(
          `CreateHypothetical: securityId ${trade.securityId} not found in first reduce`
        );
        console.error("securityObject", trade.securityId);
        return acc;
      }

      acc[trade.securityId] = {
        ...securityObject,
        postWeightWithTaxBudget: trade.postWeight,
        portfolioWeightDelta: securityObject.postWeightWithoutTaxBudget.minus(
          trade.postWeight
        ),
      };

      return acc;
    },
    withoutTaxBudgetReduce
  );

  return withTaxBudgetReduce;
}

export {
  retrieveHypotheticalAssignments,
  transformHypotheticalProposal,
  ytdNetGainsRealized,
  createTradesTable,
};
